// =============================================================================
// NAV SEARCH — forgiving, synonym-aware sidebar search matcher
// =============================================================================
// PURPOSE: Pure matching layer for the sidebar search box. Given a query and a
// list of ALREADY-RBAC-FILTERED items (the caller passes only pages the current
// role can reach), returns the matching items ranked best-first.
//
// RBAC NOTE: this file does NOT know about roles. The caller (Sidebar.tsx) filters
// SIDEBAR_ITEMS through `canAccessPage` BEFORE handing them here, so a rep can
// never surface a page they can't open. Keeping the gate at the call site (not
// here) means the search inherits the exact same gate the nav render uses.
//
// FORGIVING, NOT STRICT — four layers, in priority order:
//   1. exact / prefix / substring on label + every alias (+ spaces-stripped)
//   2. token coverage — each query word matched against target words
//   3. compact typo tolerance — Levenshtein on the spaces-stripped forms
//   4. token typo tolerance — Levenshtein per word
// Canonical cases that MUST work (see utils/__tests__/navSearch.test.ts):
//   "lost lead" / "triage" / "lead sheet" → Lost Leads #1
//   "instgram" → Instagram DMs · "leadserch" → Lead Search · "xyzzy" → []
// =============================================================================

import type { SidebarItemMeta } from "../config/sidebarItems";

export interface NavSearchResult {
  item: SidebarItemMeta;
  score: number;
}

/** lowercase + collapse whitespace. */
export function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, " ");
}

/** strip everything but a-z0-9 (spaces, hyphens, punctuation). */
function compact(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** classic Levenshtein edit distance (iterative, O(n*m)). */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/** typo threshold — longer words tolerate more edits. */
function typoThreshold(len: number): number {
  return len >= 6 ? 2 : len >= 4 ? 1 : 0;
}

/** score one query against one target string (label or a single alias). */
function scorePair(qn: string, qTokens: string[], qc: string, target: string): number {
  const tn = normalize(target);
  if (!qn || !tn) return 0;

  // Layer 1 — exact / prefix / substring on the normalized strings.
  if (tn === qn) return 100;
  if (tn.startsWith(qn)) return 90;
  if (tn.includes(qn)) return 74;
  if (qn.length >= 4 && qn.includes(tn)) return 58; // query longer than target

  // Layer 1b — compact (spaces/punct stripped) substring, catches "leadsearch".
  const tc = compact(target);
  if (tc && qc) {
    if (tc.includes(qc)) return 66;
    if (qc.length >= 4 && qc.includes(tc)) return 54;
  }

  // Layer 2 — token coverage, WEIGHTED by match quality so an exact word beats a
  // mere shared prefix (else "leadserch" would tie every "lead*" item at full
  // coverage and the real typo target loses on the alpha tiebreak).
  const tTokens = tn.split(" ").filter(Boolean);
  let coverage = 0;
  for (const qt of qTokens) {
    let best = 0;
    for (const tt of tTokens) {
      if (tt === qt) {
        best = 1;
        break;
      }
      if (tt.startsWith(qt) || qt.startsWith(tt)) best = Math.max(best, 0.6);
      else if (qt.length >= 3 && tt.includes(qt)) best = Math.max(best, 0.5);
    }
    coverage += best;
  }
  let tokenScore = 0;
  if (qTokens.length > 0 && coverage > 0) {
    coverage /= qTokens.length;
    tokenScore = 30 + coverage * 38; // exact-full = 68, prefix-full ≈ 52.8
  }

  // Layer 3 — compact typo tolerance (whole spaces-stripped forms). Ranked ABOVE
  // a prefix-only token share so a genuine typo ("leadserch"→lead search) wins.
  let typoScore = 0;
  if (qc.length >= 4 && tc.length >= 4) {
    const d = editDistance(qc, tc);
    if (d <= typoThreshold(Math.max(qc.length, tc.length))) {
      typoScore = Math.max(typoScore, 70 - d * 6); // d1 = 64, d2 = 58
    }
  }

  // Layer 4 — per-token typo tolerance.
  for (const qt of qTokens) {
    if (qt.length < 4) continue;
    for (const tt of tTokens) {
      if (tt.length < 4) continue;
      const d = editDistance(qt, tt);
      if (d <= typoThreshold(Math.max(qt.length, tt.length))) {
        typoScore = Math.max(typoScore, 46 - d * 7);
      }
    }
  }

  return Math.max(tokenScore, typoScore);
}

/**
 * Rank items against a query. Items are assumed already RBAC-filtered by the
 * caller. Returns matches (score > 0) best-first, ties broken alphabetically.
 */
export function searchNav(query: string, items: SidebarItemMeta[]): NavSearchResult[] {
  const qn = normalize(query);
  if (!qn) return [];
  const qTokens = qn.split(" ").filter(Boolean);
  const qc = compact(query);

  const results: NavSearchResult[] = [];
  for (const item of items) {
    const targets = [item.label, ...item.aliases];
    let best = 0;
    for (const t of targets) {
      const s = scorePair(qn, qTokens, qc, t);
      if (s > best) best = s;
      if (best >= 100) break;
    }
    if (best > 0) results.push({ item, score: best });
  }

  results.sort((a, b) =>
    b.score !== a.score ? b.score - a.score : a.item.label.localeCompare(b.item.label)
  );
  return results;
}
